import { CURRENCIES, PROVINCE_CODES } from '@telestore/shared';
import { z } from 'zod';

/**
 * Primitives every other contract builds on. CLAUDE.md §7: this package is the
 * single source of truth — the API validates with these, the DB stores JSONB
 * typed by these, and the frontends infer their types from these.
 */

export const uuidSchema = z.string().uuid();

export const currencySchema = z.enum([...CURRENCIES]);

export const localeSchema = z.enum(['km', 'en']);

export const verticalSchema = z.enum(['retail', 'fnb', 'services', 'digital']);

export const planSchema = z.enum(['free', 'starter', 'pro', 'business']);

/**
 * Money crosses the wire as minor units plus a currency, never as a float and
 * never as a formatted string. The client formats; the server computes.
 */
export const moneySchema = z.object({
  amount: z.number().int(),
  currency: currencySchema,
});
export type MoneyDto = z.infer<typeof moneySchema>;

export const positiveMoneySchema = moneySchema.refine((m) => m.amount >= 0, {
  message: 'Amount must not be negative',
});

export const provinceCodeSchema = z
  .string()
  .refine((code) => PROVINCE_CODES.includes(code), { message: 'Unknown province' });

/** Bilingual text. Khmer is the customer default; English is the fallback. */
export const i18nTextSchema = z.object({
  km: z.string().max(2000).default(''),
  en: z.string().max(2000).default(''),
});
export type I18nText = z.infer<typeof i18nTextSchema>;

export function pickText(text: I18nText | null | undefined, locale: 'km' | 'en'): string {
  if (!text) return '';
  const primary = locale === 'km' ? text.km : text.en;
  const fallback = locale === 'km' ? text.en : text.km;
  return primary.trim() !== '' ? primary : fallback;
}

export const imageSchema = z.object({
  url: z.string().url(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  alt: z.string().max(200).optional(),
});
export type ImageDto = z.infer<typeof imageSchema>;

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(24),
  cursor: z.string().optional(),
});

export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
}

/**
 * Acquisition attribution (§6.3 / §3 `affiliate` v1 obligation): a nullable
 * JSONB blob on the order, filled from `?src=` and `?start=ref_...` links.
 * It exists in v1 so merchant analytics has the data before the affiliate
 * module is built.
 */
export const attributionSchema = z.object({
  source: z.string().max(60).nullable().default(null),
  ref_code: z.string().max(60).nullable().default(null),
  campaign: z.string().max(120).nullable().default(null),
  landing_path: z.string().max(300).nullable().default(null),
  captured_at: z.string().datetime().nullable().default(null),
});
export type Attribution = z.infer<typeof attributionSchema>;

export const errorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
});
